import React, { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import {
    AdvertisementText,
    Button,
    Glyphicon,
    Image,
    Link,
    PageSection,
    Text,
} from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { ContentPreview } from 'reaxl-molecules';

import contentAnalyticsHandlers from '@/analytics/contentAnalyticsHandlers';

import { vdpResultsDuck } from '@/ducks/vdp';

const renderSponsoredText = (sponsoredText) => sponsoredText && (
    <AdvertisementText
        color="subdued"
        componentClass="div"
    >
        {sponsoredText}
    </AdvertisementText>
);

const renderTitle = (title) => title && (
    <Text
        weight="bold"
        componentClass="div"
        className="link-unstyled"
    >
        {/* eslint-disable-next-line react/no-danger */}
        <span dangerouslySetInnerHTML={{ __html: title }} />
    </Text>
);

const renderLink = (link, sponsoredText, cmp, onResourcesTextLinkClick = () => { }) => (
    // Renders a button that looks like a link for tagging purposes.
    // Entire section acts as the interactive link
    (
        <Button
            bsStyle="text"
            onClick={onResourcesTextLinkClick}
        >
            {cmp === 'ny_syc' && (
                <img
                // eslint-disable-next-line no-template-curly-in-string
                    src="https://ad.doubleclick.net/ddm/trackimp/N3973.1360.KELLEYBLUEBOOK/B27554392.333476565;dc_trk_aid=525411378;dc_trk_cid=100510000;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=?"
                    border="0"
                    height="1"
                    width="1"
                    alt="Advertisement"
                />
            )}
            {/* eslint-disable-next-line react/no-danger */}
            <span dangerouslySetInnerHTML={{ __html: link.label }} />
            {(!!sponsoredText || link.label === 'Finance with us') && (
                <>
                    {' '}
                    <Glyphicon glyph="offsite" />
                </>
            )}
        </Button>
    )
);

const renderImage = (image) => image && (
    <Image
        className="margin-right-sm-3"
        src={image.src}
        alt={image.alt}
        width="68"
        height="68"
    />
);

export const getContentPreview = (
    resources,
    hideAds,
    handleResourcesClick,
    handleResourcesImpression,
    handleResourcesTextLinkClick,
) => resources.filter((details) => !hideAds || !details.sponsoredText).map((details = {}) => {

    const { title, image, link, cmp, epn, sponsoredText } = details;
    const data = {
        cmp,
        epn,
    };

    const linkCtas = { ...link, rel: 'nofollow noopener' };
    if (link?.href?.includes('instant-cash-offer')) {
        linkCtas.href = link.href.includes('?') ? `${link.href}&LNX=TIMATCVDPCALCTXT` : `${link.href}?LNX=TIMATCVDPCALCTXT`;
        linkCtas.rel = 'noopener';
    }

    const onResourcesTextLinkClick = handleResourcesTextLinkClick(details);

    return (
        <DelayedImpressionObserver
            onChange={handleResourcesImpression(details)}
            key={`${cmp}-resources-delayed-impression`}
            className="col-xs-12 col-sm-5 margin-bottom-4"
            data-qaid={cmp}
        >
            <Link
                href={linkCtas.href}
                rel={linkCtas.rel}
                target="_blank"
                className="link-unstyled"
                onClick={handleResourcesClick(data)}
                undecorated
            >
                <ContentPreview
                    image={renderImage(image)}
                    category={renderSponsoredText(sponsoredText)}
                    headline={renderTitle(title)}
                    summary={renderLink(linkCtas, sponsoredText, cmp, onResourcesTextLinkClick)}
                    orientation="landscape"
                />
            </Link>
        </DelayedImpressionObserver>
    );
});

function AdditionalResourcesContainer() {
    const {
        resources: {
            additionalResources = [],
        } = {},
        finance = [],
        dealerConfig: {
            hideAds,
        } = {},
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const {
        additional_resources: [, { carchex: isCarchexEnabled }],
    } = useFeatures([
        'additional_resources',
    ]);

    const resources = [
        ...finance,
        ...additionalResources,
    ];

    const handleResourcesImpression = ({ cmp, title, link }) => () => {
        const analyticsHandlerKey = {
            get_loan_txt: isCarchexEnabled ? ['featuredPartnerImpression', 'carchex'] : ['featuredPartnerImpression', 'capitalone'],
            apply_crd_vw_wb_btn: ['featuredPartnerImpression', 'financeWithUs'],
            get_quote_txt: ['featuredPartnerImpression', 'rootInsurance'],
            ny_syc: ['featuredPartnerImpression', 'experian'],
        };
        const impressionHandler = analyticsHandlerKey[cmp];
        if (impressionHandler) {
            const data = {
                name: impressionHandler[1],
                subhead: link?.label,
                title,
            };

            if (cmp !== 'ny_syc') {
                data.heading = `additional-resources::${title}`;
            }

            sendImpressions({
                name: impressionHandler[0],
                data,
            });
        }
    };

    const handleResourcesClick = useCallback((data = {}) => (event) => {
        const analyticsHandlerKey = {
            apply_crd_vw_wb_btn: contentAnalyticsHandlers.financeWithUsLink,
            ns_tim: contentAnalyticsHandlers.kbbInstantCashClick,
            ny_syc: contentAnalyticsHandlers.creditReportClick,
            get_loan_txt: isCarchexEnabled ? contentAnalyticsHandlers.warrantyResourceClick : contentAnalyticsHandlers.capOneResourceClick,
            get_quote_txt: contentAnalyticsHandlers.rootInsuranceClick,
        };
        const clickHandler = analyticsHandlerKey[data.cmp];
        if (clickHandler) {
            event.stopPropagation();
            sendClick(clickHandler, event, data);
        }
    }, [isCarchexEnabled]);

    const handleResourcesTextLinkClick = useCallback((data = {}) => (event) => {
        // Fires tagging only for Root Insurance
        if (data.cmp === 'get_quote_txt') {
            event.stopPropagation();
            sendClick(contentAnalyticsHandlers.rootInsuranceTextLinkClick, event, data);
        }
    }, []);

    if (!resources?.length) { return null; }

    return (
        <PageSection
            className="col-lg-8"
            data-qaid="cntr-resources"
            uiContext="level-3"
            heading="More to explore on your car-buying journey"
            alignment="left"
        >
            <div className="display-flex flex-wrap text-left justify-content-between">
                {!!resources && getContentPreview(resources, hideAds, handleResourcesClick, handleResourcesImpression, handleResourcesTextLinkClick)}
            </div>
        </PageSection>
    );
}

export default AdditionalResourcesContainer;
